import { forwardRef } from 'react';
import { Stack } from '../Stack';
import { Spinner } from '../Spinner';
import { tv, VariantProps } from 'tailwind-variants';
import { twJoin } from 'tailwind-merge';
import { defaultInputClasses } from '../utils';

const textFieldVariants = tv({
  slots: {
    wrapper: '',
    label: 'font-aribaubold text-sm',
    input: twJoin('w-full border-2 text-sm font-aribau', defaultInputClasses),
  },
  variants: {
    fullWidth: {
      true: {
        wrapper: 'w-full',
      },
    },
    align: {
      inherit: {
        input: '',
        label: '',
      },
      left: {
        input: 'text-left',
        label: 'text-left',
      },
      center: {
        input: 'text-center',
        label: 'text-center',
      },
      right: {
        input: 'text-right',
        label: 'text-right',
      },
    },
    invalid: {
      true: {
        input:
          'border-red-500 bg-red-100 focus:bg-red-50 focus:border-red-500 focus:ring-red-500/50',
      },
      false: {
        input: 'border-gray-100 bg-gray-100 focus:bg-gray-50 focus:border-gray-100',
      },
    },
  },
  defaultVariants: {
    align: 'center', // for legacy reasons
    fullWidth: true,
    invalid: false,
  },
});

type TextFieldVariants = VariantProps<typeof textFieldVariants>;
type DefaultProps = Omit<React.ComponentProps<'input'>, 'invalid' | 'align'>;

interface TextFieldProps extends TextFieldVariants, DefaultProps {
  name: string;
  label?: string;
  loading?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { name, label, type, loading, invalid, align, fullWidth, className, ...props },
  ref,
): React.ReactElement {
  const { wrapper, label: labelClasses, input } = textFieldVariants();

  return (
    <Stack gap={2} className={wrapper({ fullWidth, className })}>
      {label && (
        <label htmlFor={name} className={labelClasses({ align })}>
          {label}
        </label>
      )}
      <Stack direction="row">
        <input
          ref={ref}
          type={type || 'text'}
          id={name}
          name={name}
          {...props}
          className={input({ invalid, align })}
        />
        {loading && <Spinner className="opacity-45 w-4 h-4 right-2 absolute" />}
      </Stack>
    </Stack>
  );
});
